import {
  prepareSkyLeviathanMesh,
  skyLeviathanTriangleDistance,
} from './96-sky-leviathan-contacts.mjs';

const accepted = new WeakSet();
const axes = ['x', 'y', 'z'];
const identity = (origin = { x: 0, y: 0, z: 0 }) => ({
  origin,
  right: { x: 1, y: 0, z: 0 },
  up: { x: 0, y: 1, z: 0 },
  back: { x: 0, y: 0, z: 1 },
});

/** Resolve an exported rigid bone to its existing canonical100 driver. All
 * positions are world metres; the imported rig's rest-axis offset is UI-owned. */
export function skyCathedralArtFrame(pose, bone) {
  if (bone === 'Cathedral') return identity();
  if (bone === 'Chassis') return pose.chassis;
  if (bone.startsWith('part__'))
    return (
      (pose.canonicalParts ?? pose.parts).find((part) => part.id === bone.slice(6))?.frame ?? null
    );
  const split = bone.split('__');
  const point = pose.legs?.[split[0]]?.[split[1]];
  return point && axes.every((axis) => Number.isFinite(point[axis])) ? identity(point) : null;
}

/** Compile actual unit-weight skin surfaces into immutable canonical-driver
 * triangles. No mesh bounds become solid collision. Malformed, missing target
 * surfaces or over-budget artwork fails readiness, never falls back to boxes.
 * Facets with no surface under the contact solver's existing numerical policy
 * are reported separately; they cannot invalidate every real face of a hull. */
export function createSkyCathedralCollision(source) {
  if (!Array.isArray(source) || !source.length || source.length > 128) return null;
  const ids = new Set();
  let count = 0;
  const parts = [];
  const discardedDegenerateFacets = [];
  for (const item of source) {
    if (
      !item ||
      typeof item.bone !== 'string' ||
      !/^[\w-]{1,100}$/.test(item.bone) ||
      ids.has(item.bone) ||
      !Array.isArray(item.triangles) ||
      !item.triangles.length
    )
      return null;
    ids.add(item.bone);
    const halfExtents = { x: 0, y: 0, z: 0 };
    const triangles = [];
    for (const [triangleIndex, tri] of item.triangles.entries()) {
      if (++count > 50000) return null;
      if (!tri || typeof tri !== 'object') return null;
      const copy = {};
      for (const key of ['a', 'b', 'c']) {
        if (
          !tri[key] ||
          !axes.every((axis) => Number.isFinite(tri[key][axis]) && Math.abs(tri[key][axis]) <= 150)
        )
          return null;
        copy[key] = Object.freeze(
          Object.fromEntries(
            axes.map((axis) => {
              halfExtents[axis] = Math.max(halfExtents[axis], Math.abs(tri[key][axis]));
              return [axis, tri[key][axis]];
            }),
          ),
        );
      }
      Object.freeze(copy);
      if (Number.isFinite(skyLeviathanTriangleDistance(copy.a, copy))) triangles.push(copy);
      else discardedDegenerateFacets.push(Object.freeze({ bone: item.bone, triangleIndex }));
    }
    Object.freeze(triangles);
    if (!triangles.length || !prepareSkyLeviathanMesh(triangles)) return null;
    parts.push(
      Object.freeze({ bone: item.bone, triangles, halfExtents: Object.freeze(halfExtents) }),
    );
  }
  for (const name of [
    'front-left-mechanism',
    'front-right-mechanism',
    'rear-left-mechanism',
    'rear-right-mechanism',
    'middle-left-mechanism',
    'middle-right-mechanism',
    'generator',
    'generator-shutter',
  ])
    if (!ids.has(`part__${name}`)) return null;
  const handle = Object.freeze({
    parts: Object.freeze(parts),
    triangles: count - discardedDegenerateFacets.length,
    sourceTriangles: count,
    discardedDegenerateFacets: Object.freeze(discardedDegenerateFacets),
  });
  accepted.add(handle);
  return handle;
}

/** Replace provisional solids with every visible source surface in the same
 * posed frame. Original parts remain available solely as articulation drivers.
 * Surface triangles, not broad bounds, own pilot and projectile contact. */
export function poseSkyCathedralArtwork(handle, pose) {
  if (!accepted.has(handle) || !pose.valid)
    return { ...pose, valid: false, failures: ['unprepared-cathedral-art'] };
  const originals = new Map(pose.parts.map((part) => [part.id, part]));
  const parts = handle.parts.map((part) => {
    const id = part.bone.startsWith('part__') ? part.bone.slice(6) : `art-${part.bone}`;
    const frame = skyCathedralArtFrame(pose, part.bone);
    if (!frame) throw new Error(`Missing Cathedral driver: ${part.bone}`);
    return {
      ...originals.get(id),
      id,
      kind: originals.get(id)?.kind ?? 'stone',
      frame,
      triangles: part.triangles,
      halfExtents: part.halfExtents,
      authoredSurface: true,
      collisionEnabled: originals.get(id)?.collisionEnabled !== false,
    };
  });
  return { ...pose, canonicalParts: pose.parts, parts, artwork: true };
}

/** Bind prepared source geometry before campaign arrival. Refresh is allowed
 * while frozen, but active encounters cannot exchange physical surfaces. */
export function installSkyCathedralCollision(state, handle) {
  if (
    !state ||
    !accepted.has(handle) ||
    (state.status === 'playing' && state.cathedral?.age > 0 && state.cathedralCollision !== handle)
  )
    return false;
  state.cathedralCollision = handle;
  if (state.cathedral) state.cathedral.geometry = handle;
  return true;
}
